import React, { ChangeEvent } from 'react';

import { css } from '@emotion/react';
import { SelectionRowRadioGroupProvider } from '@robinhood/design';
import { CssVars } from '@robinhood/design/product/themes';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';

import AddressCell from './AddressCell';
import logger from 'src/clients/LoggingClient';
import { AddressChallengePageMutation as AddressChallengePageMutationType } from 'src/clients/Network/gql/build/AddressChallengePageMutation.graphql';
import { AddressChallengePageQuery as AddressChallengePageQueryType } from 'src/clients/Network/gql/build/AddressChallengePageQuery.graphql';
import Button from 'src/components/creditApplication/Button';
import Cell from 'src/components/creditApplication/Cell';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import GetHelpTextButton from 'src/components/creditApplication/GetHelpTextButton';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import { showGlobalErrorAlert, useGlobalStore } from 'src/state/global';
import { mkStreetAddress } from 'src/utils/address';
import { useAsyncMutation } from 'src/utils/graphql';
import LoadingPage from 'src/views/creditApplication/LoadingPage';

const AddressChallengePageQuery = graphql`
  query AddressChallengePageQuery($identityId: String!) {
    addressChallenge(q: { identityId: $identityId }) {
      addresses {
        id
        ...AddressCellFragment
        primaryAddressLine
        secondaryAddressLine
        locality
        subdivision
        zip
      }
    }
  }
`;

const AddressChallengePageMutation = graphql`
  mutation AddressChallengePageMutation($q: SubmitAddressChallengeInput!) {
    submitAddressChallenge(q: $q) {
      data {
        success
      }
      errors
    }
  }
`;

const AddressChallengePage = () => {
  const identityId = useGlobalStore((s) => s.identityId);
  const {
    addressChallenge: { addresses },
  } = useLazyLoadQuery<AddressChallengePageQueryType>(
    AddressChallengePageQuery,
    { identityId: identityId ?? '' },
    { fetchPolicy: 'network-only' },
  );
  const { underwrite, underwriteLoading } = React.useContext(NavigationContext);
  const [addressId, setAddressId] = React.useState<string | null>(null);
  const [commitChallenge, challengeLoading] =
    useAsyncMutation<AddressChallengePageMutationType>(AddressChallengePageMutation);
  const address = addresses.find((address) => address.id === addressId);
  const loading = challengeLoading || underwriteLoading;

  const onSubmit = React.useCallback(async () => {
    try {
      if (!!identityId && address != null) {
        const res = await commitChallenge({
          variables: {
            q: {
              identityId,
              streetAddress: mkStreetAddress(address.primaryAddressLine, address.secondaryAddressLine),
              locality: address.locality,
              subdivision: address.subdivision,
              zip: address.zip,
            },
          },
        });
        if (res.submitAddressChallenge.errors.length > 0) {
          showGlobalErrorAlert();
        } else {
          await underwrite();
        }
      }
    } catch (e) {
      logger.error(e, { source: 'AddressChallengePage.onSubmit' });
      showGlobalErrorAlert();
    }
  }, [commitChallenge, identityId, address, underwrite]);

  const buttonState = React.useMemo(() => {
    return loading
      ? 'loading'
      : addressId != null && addresses.some((address) => address.id === addressId)
        ? 'default'
        : 'disabled';
  }, [addressId, addresses, loading]);

  const onChangeAddressId = React.useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAddressId(e.target.value);
  }, []);

  return (
    <CreditApplicationPageLayout
      showLogo={false}
      title='Confirm your past&nbsp;address'
      subtitle='For your security, please confirm which of these addresses you have lived&nbsp;in:'
    >
      <div css={root}>
        <Cell css={cell}>
          <SelectionRowRadioGroupProvider value={addressId} onChange={onChangeAddressId}>
            {addresses.map((address, index) => (
              <>
                {index > 0 && <div css={divider} />}
                <AddressCell address={address} />
              </>
            ))}
          </SelectionRowRadioGroupProvider>
        </Cell>
        <div css={footer}>
          <Button label='Confirm' state={buttonState} width='full' type='submit' onClick={onSubmit} />
          <GetHelpTextButton />
        </div>
      </div>
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 24px;
  gap: 40px;
  text-align: center;
`;

const cell = css`
  padding: 0px;
  overflow: hidden;
`;

const footer = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: center;
  width: 100%;
`;

const divider = css`
  border-top: 1px solid ${CssVars.neutral.bg3};
`;

const withSuspense = () => {
  return (
    <React.Suspense fallback={<LoadingPage />}>
      <AddressChallengePage />
    </React.Suspense>
  );
};

export default withSuspense;
